/**
 * <Icon /> — typed wrapper around the 240 Ricon icon components.
 *
 * Usage:
 *   <Icon name="bell" size={20} className="text-foreground/62" />
 *   <Icon name="heart" variant="bold" size={16} />
 *
 * Defaults:
 *   variant = "outline"   (matches Sora's outline-default / fill-on-active pattern)
 *   size    = 20          (Sora's standalone icon default)
 *
 * Icons inherit `currentColor` from text — set color via Tailwind classes like
 * `text-foreground`, `text-foreground/62`, `text-accent-blue`, etc.
 *
 * Source: src/shared/components/icons/ricon/{outline,bold}.tsx — generated from
 * the Ricon SVG library. Re-run scripts/generate-ricon-components.mjs to add
 * new icons.
 */
import type { JSX, SVGProps } from "react"
import * as Bold from "./ricon/runtime-bold"
import * as Outline from "./ricon/runtime-outline"
import type { IconName } from "./ricon/icon-names"

type IconComponent = (props: Outline.IconSvgProps) => JSX.Element

// Keep the string-based public API while allowing the bundler to discard the
// dozens of generated icons that no production surface references. The full
// catalog remains available to the development-only brand showcase.
const RUNTIME_ICON_COMPONENTS: Partial<
  Record<IconName, { outline: IconComponent; bold: IconComponent }>
> = {
  "arrow-down": { outline: Outline.ArrowDown, bold: Bold.ArrowDown },
  "arrow-down-2": { outline: Outline.ArrowDown2, bold: Bold.ArrowDown2 },
  "arrow-left": { outline: Outline.ArrowLeft, bold: Bold.ArrowLeft },
  "arrow-left-2": { outline: Outline.ArrowLeft2, bold: Bold.ArrowLeft2 },
  "arrow-right": { outline: Outline.ArrowRight, bold: Bold.ArrowRight },
  "arrow-right-2": { outline: Outline.ArrowRight2, bold: Bold.ArrowRight2 },
  "arrow-up-2": { outline: Outline.ArrowUp2, bold: Bold.ArrowUp2 },
  attach: { outline: Outline.Attach, bold: Bold.Attach },
  "attention-circle": {
    outline: Outline.AttentionCircle,
    bold: Bold.AttentionCircle,
  },
  "attention-triangle": {
    outline: Outline.AttentionTriangle,
    bold: Bold.AttentionTriangle,
  },
  bag: { outline: Outline.Bag, bold: Bold.Bag },
  "bag-1": { outline: Outline.Bag1, bold: Bold.Bag1 },
  bell: { outline: Outline.Bell, bold: Bold.Bell },
  camera: { outline: Outline.Camera, bold: Bold.Camera },
  category: { outline: Outline.Category, bold: Bold.Category },
  "chart-square": { outline: Outline.ChartSquare, bold: Bold.ChartSquare },
  chat: { outline: Outline.Chat, bold: Bold.Chat },
  clock: { outline: Outline.Clock, bold: Bold.Clock },
  edit: { outline: Outline.Edit, bold: Bold.Edit },
  flash: { outline: Outline.Flash, bold: Bold.Flash },
  graph: { outline: Outline.Graph, bold: Bold.Graph },
  heart: { outline: Outline.Heart, bold: Bold.Heart },
  home: { outline: Outline.Home, bold: Bold.Home },
  image: { outline: Outline.Image, bold: Bold.Image },
  "image-1": { outline: Outline.Image1, bold: Bold.Image1 },
  "info-circle": { outline: Outline.InfoCircle, bold: Bold.InfoCircle },
  lable: { outline: Outline.Lable, bold: Bold.Lable },
  location: { outline: Outline.Location, bold: Bold.Location },
  lock: { outline: Outline.Lock, bold: Bold.Lock },
  logout: { outline: Outline.Logout, bold: Bold.Logout },
  message: { outline: Outline.Message, bold: Bold.Message },
  monitor: { outline: Outline.Monitor, bold: Bold.Monitor },
  "monitor-1": { outline: Outline.Monitor1, bold: Bold.Monitor1 },
  notebook: { outline: Outline.Notebook, bold: Bold.Notebook },
  "paper-note": { outline: Outline.PaperNote, bold: Bold.PaperNote },
  phone: { outline: Outline.Phone, bold: Bold.Phone },
  "qustion-circle": {
    outline: Outline.QustionCircle,
    bold: Bold.QustionCircle,
  },
  row: { outline: Outline.Row, bold: Bold.Row },
  save: { outline: Outline.Save, bold: Bold.Save },
  search: { outline: Outline.Search, bold: Bold.Search },
  setting: { outline: Outline.Setting, bold: Bold.Setting },
  shield: { outline: Outline.Shield, bold: Bold.Shield },
  "shield-done": { outline: Outline.ShieldDone, bold: Bold.ShieldDone },
  "shield-fail": { outline: Outline.ShieldFail, bold: Bold.ShieldFail },
  slider: { outline: Outline.Slider, bold: Bold.Slider },
  tick: { outline: Outline.Tick, bold: Bold.Tick },
  "tick-circle": { outline: Outline.TickCircle, bold: Bold.TickCircle },
  "trash-bin": { outline: Outline.TrashBin, bold: Bold.TrashBin },
  upload: { outline: Outline.Upload, bold: Bold.Upload },
  user: { outline: Outline.User, bold: Bold.User },
  users: { outline: Outline.Users, bold: Bold.Users },
  video: { outline: Outline.Video, bold: Bold.Video },
  voice: { outline: Outline.Voice, bold: Bold.Voice },
  volume: { outline: Outline.Volume, bold: Bold.Volume },
  "volume-high": { outline: Outline.VolumeHigh, bold: Bold.VolumeHigh },
  x: { outline: Outline.X, bold: Bold.X },
  "x-circle": { outline: Outline.XCircle, bold: Bold.XCircle },
}

export type IconVariant = "outline" | "bold"

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  /** Kebab-case icon name. Type-checked against the 120-icon Ricon set. */
  name: IconName
  /** Outline (default) or filled. Pair them — outline for default state, bold for active. */
  variant?: IconVariant
  /** Width and height in pixels. Defaults to 20. */
  size?: number
}

export function Icon({ name, variant = "outline", size = 20, ...rest }: IconProps) {
  const variants = RUNTIME_ICON_COMPONENTS[name]
  const Component = variants?.[variant]
  if (!Component) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`<Icon /> unknown name "${name}" (variant "${variant}")`)
    }
    return null
  }
  // The codegen-emitted components share the IconSvgProps shape; the cast keeps
  // the narrower public API (`IconProps`) without leaking the codegen type.
  return <Component size={size} {...rest} />
}

export type { IconName }
